import type { AssetManifest } from '@kenocyber/shared';
import {
  baseRadiusFor,
  MINING_FLEET_LIMIT,
  PLANET_SCALE_DENOMINATOR_BPS,
  ringLayout,
  ringRadiusFactor,
  ringsForCount,
  shipWidthFor,
} from '@kenocyber/shared';
import Phaser from 'phaser';
import { staticAssetUrl } from './assets';
import type { GameBridge } from './bridge';

/**
 * The mining stage: a fleet on expanding rings, breaking a planet.
 *
 * ## One canvas, one renderer, one game
 *
 * This is a second Phaser SCENE, not a second game. It is handed to the one
 * `Phaser.Game` at boot alongside the hunt, so there is exactly one canvas and
 * exactly one WebGL context for the life of the tab. Nothing here starts, stops
 * or switches a scene: opening and closing the window is a MESSAGE, and what it
 * changes is which objects exist - never the canvas, never the renderer, and
 * never the hunt, which keeps ticking behind it.
 *
 * ## It draws inside a hole in the modal
 *
 * The mining window is one window. Its controls are DOM; its stage is a
 * transparent rectangle in the middle of them, and this scene draws through it.
 * The rectangle arrives as `mining:stage` in canvas pixels and the camera runs
 * at 1:1, so "centred in the stage" is the stage's own centre rather than a
 * conversion between two coordinate systems that could disagree.
 *
 * ## It draws what the server said, and cannot pay anybody
 *
 * The rings come from the last authoritative fleet; the planet's size and
 * health come from the last authoritative planet. If this file drew a hundred
 * beams the account would earn the same and the planet would break at the same
 * instant: production is `Power x seconds` and damage is `Power x seconds`,
 * both on the server, and there is no call from here into anything that could
 * credit or destroy a thing.
 *
 * ## Pooled, and given back
 *
 * Every particle comes from a pool sized once per rebuild; nothing is allocated
 * in a frame. Every subscription is handed back on shutdown - a scene that kept
 * listening after it died once took the whole application down with it.
 */

/** How much of the stage's half-size the outermost ring may use. */
const RING_STAGE_FRACTION = 0.92;
/** The widest a docked hull is ever drawn. It shrinks when the rings do. */
const SHIP_MAX_WIDTH = 56;
/**
 * How much detail a fleet of a given size gets.
 *
 * The SHIPS are never reduced - every docked hull is drawn at every size, and
 * Power, production and damage are untouched by anything in this file. What
 * thins out is the decoration: a hundred beams with four sparks each is four
 * hundred objects moved every frame, and past a certain density nobody can see
 * an individual spark anyway.
 *
 * Measured at a hundred ships: 1007 scene objects and a frame rate at 11
 * against 27 with the window shut.
 */
function detailFor(count: number): {
  particlesPerBeam: number;
  /** One bloom per ship below this, a few shared ones above it. */
  perShipImpacts: boolean;
} {
  if (count <= 24) {
    return { particlesPerBeam: 4, perShipImpacts: true };
  }
  if (count <= 64) {
    return { particlesPerBeam: 2, perShipImpacts: false };
  }
  return { particlesPerBeam: 1, perShipImpacts: false };
}

/** A plain vector, which is what Phaser 4's  takes. */
function toPoint(x: number, y: number): Phaser.Math.Vector2 {
  return new Phaser.Math.Vector2(x, y);
}

/** Shared blooms drawn at the planet's edge when the fleet is too dense for one each. */
const SHARED_IMPACTS = 8;
/** Particles orbiting the planet. */
const ORBITAL_PARTICLES = 26;
/** Particles running the window's neon frame. */
const FRAME_PARTICLES = 28;
/** Mineral speckles on the planet's surface. */
const SURFACE_SPECKLES = 44;
/** Luminous cracks across it. */
const PLANET_CRACKS = 7;

/** Depths, in one place, so "what is in front of what" is answerable. */
const DEPTH = {
  curtain: 100,
  frame: 101,
  rings: 102,
  planetHalo: 103,
  planetShell: 104,
  planetSurface: 105,
  planetCracks: 106,
  beams: 107,
  shipHalo: 108,
  ship: 109,
  spark: 110,
  impact: 111,
  orbital: 112,
} as const;

interface DockedShip {
  readonly instanceId: string;
  readonly shipIndex: number;
  readonly assetKey: string;
}

interface PlanetState {
  readonly tier: number;
  readonly remainingHealth: string;
  readonly maxHealth: string;
  readonly visualScaleBps: number;
  readonly maxStageFractionBps: number;
  readonly palette: string;
}

interface StageRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface MiningSceneOptions {
  readonly bridge: GameBridge;
  readonly manifest: AssetManifest;
}

export class MiningScene extends Phaser.Scene {
  static readonly KEY = 'mining';

  private readonly options: MiningSceneOptions;

  private ships: readonly DockedShip[] = [];
  private planet: PlanetState | null = null;
  private stage: StageRect | null = null;
  private selectedInstanceId: string | null = null;
  private shown = false;

  /** Everything the stage owns. Cleared on close, never the canvas. */
  private drawn: Phaser.GameObjects.GameObject[] = [];
  private curtain: Phaser.GameObjects.Rectangle | null = null;
  private frame: Phaser.GameObjects.Graphics | null = null;
  private beams: Phaser.GameObjects.Graphics | null = null;
  private selectedBeam: Phaser.GameObjects.Graphics | null = null;
  private cracks: Phaser.GameObjects.Graphics | null = null;
  private planetCore: Phaser.GameObjects.Arc | null = null;
  private planetHalo: Phaser.GameObjects.Arc | null = null;
  private planetShells: Phaser.GameObjects.Arc[] = [];
  private planetCentre = { x: 0, y: 0, radius: 0 };
  private shipViews: {
    readonly instanceId: string;
    readonly sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
    readonly halo: Phaser.GameObjects.Arc;
    readonly x: number;
    readonly y: number;
  }[] = [];
  private sparks: Phaser.GameObjects.Arc[] = [];
  private sparkProgress: number[] = [];
  private impacts: Phaser.GameObjects.Arc[] = [];
  private orbitals: {
    node: Phaser.GameObjects.Arc;
    angle: number;
    radius: number;
    speed: number;
  }[] = [];
  private frameParticles: { node: Phaser.GameObjects.Arc; progress: number; speed: number }[] = [];
  private elapsedMs = 0;
  /** When the fissures were last rebuilt. They breathe slowly; frames do not. */
  private cracksDrawnAtMs = -1_000;
  /** True while a hull-texture batch is in flight. Stops a second batch. */
  private loadingTextures = false;
  /** Handed back on shutdown, so a dead scene stops hearing the bridge. */
  private readonly unsubscribers: (() => void)[] = [];

  constructor(options: MiningSceneOptions) {
    /*
     * ACTIVE from boot, and drawing nothing until it is told to.
     *
     * Starting inactive would mean calling `scene.start` later, which is scene
     * management this deliberately avoids: an empty scene costs a no-op update
     * and cannot possibly disturb the arena.
     */
    super({ key: MiningScene.KEY, active: true });
    this.options = options;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    this.applyCamera();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.applyCamera, this);

    this.unsubscribers.push(
      this.options.bridge.onGameMessage('mining:state', ({ ships, planet }) => {
        this.ships = ships.map((ship) => ({
          instanceId: ship.instanceId,
          shipIndex: ship.shipIndex,
          assetKey: ship.assetKey,
        }));
        this.planet = planet;
        if (this.shown) {
          this.rebuild();
        }
      }),
      this.options.bridge.onGameMessage('mining:stage', (rect) => {
        this.stage = rect;
        if (this.shown) {
          this.rebuild();
        }
      }),
      this.options.bridge.onGameMessage('mining:select', ({ instanceId }) => {
        this.selectedInstanceId = instanceId;
        this.paintSelection();
      }),
      this.options.bridge.onGameMessage('mining:visible', ({ visible }) => {
        this.shown = visible;
        if (visible) {
          this.rebuild();
        } else {
          // Only the mining objects. The scene, the canvas and the renderer all
          // survive, and so does the hunt drawing behind them.
          this.clear();
        }
      }),
    );

    /*
     * Letting go of the bridge when the scene goes.
     *
     * A subscription outlives the object that made it unless something takes it
     * back, and this one nearly took the game down: React's StrictMode mounts
     * twice in development, so a first scene was destroyed while still
     * listening, the next message reached it, and drawing through a dead
     * factory threw all the way back into the React effect that had sent it.
     */
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      for (const unsubscribe of this.unsubscribers) {
        unsubscribe();
      }
      this.unsubscribers.length = 0;
      this.scale.off(Phaser.Scale.Events.RESIZE, this.applyCamera, this);
      this.clear();
    });
  }

  /**
   * The camera, at 1:1 over the whole canvas.
   *
   * Deliberately NOT the hunt's fitted arena. This scene draws inside a hole in
   * a DOM modal, and the hole is measured in canvas pixels - so the honest
   * thing is to work in canvas pixels. A zoom here would mean every rectangle
   * React measured had to be converted, and a conversion is a thing that can be
   * wrong in one direction only when somebody resizes the window.
   */
  private applyCamera(): void {
    const width = Math.max(1, this.scale.width);
    const height = Math.max(1, this.scale.height);
    const camera = this.cameras.main;
    camera.setSize(width, height);
    camera.setZoom(1);
    camera.setScroll(0, 0);
    if (this.shown) {
      this.rebuild();
    }
  }

  /** Destroys every object the stage owns. Called on close and before a rebuild. */
  private clear(): void {
    for (const object of this.drawn) {
      object.destroy();
    }
    this.drawn = [];
    this.shipViews = [];
    this.sparks = [];
    this.sparkProgress = [];
    this.impacts = [];
    this.orbitals = [];
    this.frameParticles = [];
    this.planetShells = [];
    this.curtain = null;
    this.frame = null;
    this.beams = null;
    this.selectedBeam = null;
    this.cracks = null;
    this.planetCore = null;
    this.planetHalo = null;
  }

  private track<T extends Phaser.GameObjects.GameObject>(object: T): T {
    this.drawn.push(object);
    return object;
  }

  /**
   * Rebuilds everything for the current fleet, planet and stage.
   *
   * Torn down and rebuilt whole rather than diffed: it happens when the server
   * answers or the window is resized, which is a handful of times a session,
   * and a diff would be a second description of the stage that could disagree
   * with this one.
   */
  private rebuild(): void {
    /*
     * Nothing is drawn by a scene that is no longer running.
     *
     * The unsubscribe on shutdown is what makes this unreachable; this is the
     * belt to those braces, because the failure it prevents is not "the rings
     * are missing" but "the exception unmounts the application".
     */
    if (this.sys?.settings?.status !== Phaser.Scenes.RUNNING) {
      return;
    }
    this.clear();
    if (!this.shown || !this.stage) {
      return;
    }
    const stage = this.stage;
    const centreX = stage.x + stage.width / 2;
    const centreY = stage.y + stage.height / 2;
    const available = Math.max(40, Math.min(stage.width, stage.height) / 2);

    /*
     * A dimmer over the ARENA, inside the canvas.
     *
     * Here rather than on the DOM backdrop so the stage stays bright: a
     * translucent element over the whole play area would dim the planet showing
     * through the hole as well, which is the one thing that must not be dim.
     */
    this.curtain = this.track(
      this.add
        .rectangle(
          this.scale.width / 2,
          this.scale.height / 2,
          this.scale.width,
          this.scale.height,
          0x04040a,
          0.82,
        )
        .setDepth(DEPTH.curtain),
    );

    /*
     * The geometry, computed once and handed to everything that draws.
     *
     * The rings and the planet used to size themselves independently, and at a
     * high tier the planet's halo reached into the innermost ring - a fleet
     * apparently standing inside the thing it was mining. The innermost radius
     * is the ceiling for the planet's outer glow, so the two can no longer
     * disagree about who owns which part of the stage.
     */
    const count = Math.min(this.ships.length, MINING_FLEET_LIMIT);
    const baseRadius = baseRadiusFor(count, available * RING_STAGE_FRACTION);

    // Textures first: the stand-ins below are only for the frame or two
    // before the batch lands, rather than for every ship on every rebuild.
    this.ensureHullTextures();

    this.buildFrame(stage);
    this.buildRings(centreX, centreY, baseRadius, count);
    this.buildPlanet(centreX, centreY, available, baseRadius);
    this.buildFleet(centreX, centreY, baseRadius, count);
    this.paintStaticBeams();
    this.paintSelection();
  }

  /**
   * Draws every beam ONCE.
   *
   * The docked fleet does not move while the window is open - the ships are on
   * fixed slots and the planet is at the centre - so a hundred lines redrawn
   * sixty times a second were a hundred lines that had not changed. The travel
   * is carried by the sparks, which do move, and by the alpha pulse below.
   */
  private paintStaticBeams(): void {
    const beams = this.beams;
    if (!beams) {
      return;
    }
    const { x: centreX, y: centreY } = this.planetCentre;
    beams.clear();
    beams.lineStyle(1.6, 0x67e8f9, 0.45);
    for (const view of this.shipViews) {
      beams.lineBetween(view.x, view.y, centreX, centreY);
    }
  }

  /**
   * The neon frame, and the particles running it.
   *
   * Drawn in the canvas rather than as a CSS border on the stage element: these
   * particles have to sit in the same space as the beams they belong with, and
   * a DOM animation over a WebGL canvas is two clocks for one effect.
   */
  private buildFrame(stage: StageRect): void {
    /*
     * Stroked ONCE; the pulse is the object's alpha.
     *
     * It used to `clear()` and re-stroke two rounded rectangles every frame,
     * which rebuilds the geometry buffer sixty times a second for a shape that
     * never changes. The glow looks the same - it is the same colours at the
     * same alphas - and the per-frame work is one number.
     */
    const frame = this.track(this.add.graphics().setDepth(DEPTH.frame));
    frame.lineStyle(2, 0x38bdf8, 1);
    frame.strokeRoundedRect(stage.x, stage.y, stage.width, stage.height, 14);
    frame.lineStyle(1, 0x67e8f9, 0.5);
    frame.strokeRoundedRect(stage.x + 5, stage.y + 5, stage.width - 10, stage.height - 10, 10);
    this.frame = frame;
    for (let index = 0; index < FRAME_PARTICLES; index += 1) {
      this.frameParticles.push({
        node: this.track(
          this.add
            .circle(stage.x, stage.y, 2.4, 0x67e8f9, 0.9)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setDepth(DEPTH.frame),
        ),
        // Spread around the perimeter, and moving at slightly different rates
        // so they never form a marching line.
        progress: index / FRAME_PARTICLES,
        speed: 0.055 + (index % 5) * 0.006,
      });
    }
  }

  /** One set of markings per ring the fleet actually occupies. */
  private buildRings(centreX: number, centreY: number, baseRadius: number, count: number): void {
    const rings = Math.max(1, ringsForCount(count));
    const graphics = this.track(this.add.graphics().setDepth(DEPTH.rings));
    for (let index = 0; index < rings; index += 1) {
      const radius = ringRadiusFactor(index) * baseRadius;
      // The innermost is brightest: it is the one a small fleet sits on, and a
      // single ring should not look like a faint outer one.
      const emphasis = index === 0 ? 1 : 0.55;
      graphics.lineStyle(2, 0x2f6f8f, 0.5 * emphasis);
      graphics.strokeCircle(centreX, centreY, radius);
      graphics.lineStyle(1, 0x2f6f8f, 0.22 * emphasis);
      graphics.strokeCircle(centreX, centreY, radius + 18);
      graphics.strokeCircle(centreX, centreY, radius - 18);
      /*
       * The modules are DRAWN, not forty objects per ring.
       *
       * They never move, never change colour and never animate, so they belong
       * in the same static graphics as the circles they sit on. As objects they
       * were forty per ring - two hundred at five rings - each costing a
       * transform every frame for a square that had not changed since it was
       * created. Same size, same colours, same alphas, same positions.
       */
      const modules = 40;
      for (let module = 0; module < modules; module += 1) {
        const angle = (module / modules) * Math.PI * 2;
        const half = (module % 5 === 0 ? 8 : 4) / 2;
        const x = centreX + Math.cos(angle) * (radius + 18);
        const y = centreY + Math.sin(angle) * (radius + 18);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        graphics.fillStyle(0x38bdf8, (module % 5 === 0 ? 0.55 : 0.28) * emphasis);
        // The square's four corners, turned to face the planet exactly as
        // `setRotation` did.
        graphics.fillPoints(
          [
            toPoint(x + (-half * cos - -half * sin), y + (-half * sin + -half * cos)),
            toPoint(x + (half * cos - -half * sin), y + (half * sin + -half * cos)),
            toPoint(x + (half * cos - half * sin), y + (half * sin + half * cos)),
            toPoint(x + (-half * cos - half * sin), y + (-half * sin + half * cos)),
          ],
          true,
        );
      }
    }
  }

  /**
   * The planet: a SPHERE, and never anything with a point on it.
   *
   * Every part is either a circle or a segment strictly inside the outer
   * radius, so the silhouette is exactly one circle however many layers are
   * added. That is a property of the construction rather than of the artwork -
   * the shape this replaced was built from spokes and read as a star.
   *
   * Concentric shells rather than one flat disc, because an unlit circle has no
   * falloff and reads as a sticker.
   */
  private buildPlanet(
    centreX: number,
    centreY: number,
    available: number,
    baseRadius: number,
  ): void {
    const planet = this.planet;
    const fractionBps = planet
      ? Math.min(planet.maxStageFractionBps, planet.visualScaleBps * 0.234)
      : 2_340;
    /*
     * Its size from the tier, and never past the innermost ring.
     *
     * The halo is drawn at 1.45x the body, so the body is capped at a fraction
     * of the first ring that keeps the whole glow inside it. Without the cap a
     * tier 6 reached under the fleet, which reads as ships standing on the
     * thing they are mining.
     */
    const fromTier = (available * fractionBps) / PLANET_SCALE_DENOMINATOR_BPS;
    const radius = Math.max(18, Math.min(fromTier, baseRadius * 0.6));
    const tint = planet ? Phaser.Display.Color.HexStringToColor(planet.palette).color : 0x67e8f9;
    this.planetCentre = { x: centreX, y: centreY, radius };

    // The halo: the light it throws, well outside the body itself.
    this.planetHalo = this.track(
      this.add
        .circle(centreX, centreY, radius * 1.45, tint, 0.09)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(DEPTH.planetHalo),
    );
    // Shells, dimmest outward, so the edge falls off instead of stopping.
    for (const [scale, alpha] of [
      [1.24, 0.12],
      [1.11, 0.18],
    ] as const) {
      this.planetShells.push(
        this.track(
          this.add
            .circle(centreX, centreY, radius * scale, tint, alpha)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setDepth(DEPTH.planetShell),
        ),
      );
    }
    // The body.
    this.planetCore = this.track(
      this.add.circle(centreX, centreY, radius, tint, 0.9).setDepth(DEPTH.planetShell),
    );

    /*
     * The mineral surface: speckles laid on the body, every one of them wholly
     * inside it.
     *
     * Placed by a deterministic walk rather than by `Math.random`, so two tabs
     * showing the same planet show the same planet. Nothing here reaches the
     * simulation, but a surface that shimmered differently per tab would look
     * like a bug and be impossible to screenshot twice.
     */
    for (let index = 0; index < SURFACE_SPECKLES; index += 1) {
      const angle = index * 2.399963; // the golden angle: an even, non-repeating spread
      const distance = Math.sqrt((index + 0.5) / SURFACE_SPECKLES) * radius * 0.82;
      const size = radius * (index % 4 === 0 ? 0.09 : 0.055);
      this.track(
        this.add
          .circle(
            centreX + Math.cos(angle) * distance,
            centreY + Math.sin(angle) * distance,
            size,
            index % 3 === 0 ? 0xffffff : tint,
            index % 3 === 0 ? 0.16 : 0.26,
          )
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(DEPTH.planetSurface),
      );
    }

    // Luminous cracks, drawn every frame so they can breathe. Their endpoints
    // stay inside the body, so the silhouette is still exactly the circle.
    this.cracks = this.track(this.add.graphics().setDepth(DEPTH.planetCracks));

    // Particles pulled in towards it, and orbiting it.
    for (let index = 0; index < ORBITAL_PARTICLES; index += 1) {
      const orbit = radius * (1.25 + (index % 5) * 0.16);
      this.orbitals.push({
        node: this.track(
          this.add
            .circle(centreX, centreY, index % 4 === 0 ? 2.6 : 1.8, tint, 0.8)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setDepth(DEPTH.orbital),
        ),
        angle: (index / ORBITAL_PARTICLES) * Math.PI * 2,
        radius: orbit,
        speed: 0.00042 + (index % 3) * 0.00013,
      });
    }
  }

  /**
   * Loads every hull texture the fleet needs, ONCE, and rebuilds once after.
   *
   * This replaced a loader call inside the per-ship loop. Each ship whose
   * texture was missing queued its own `COMPLETE` handler, and each handler
   * rebuilt the WHOLE stage - so a hundred ships meant up to a hundred full
   * rebuilds of nine hundred objects, which is where "the page freezes for
   * several seconds" came from. Measured: 169 ms of longest task at one ship,
   * 7 393 ms at seventy-two.
   *
   * Distinct assets, not ships: twenty hull types cover any fleet size, so the
   * work is bounded by the catalogue rather than by how many ships are docked.
   */
  private ensureHullTextures(): boolean {
    const missing = new Map<string, string>();
    for (const ship of this.ships.slice(0, MINING_FLEET_LIMIT)) {
      const textureKey = `mining-${ship.assetKey}`;
      if (this.textures.exists(textureKey) || missing.has(textureKey)) {
        continue;
      }
      const url = staticAssetUrl(this.options.manifest, ship.assetKey);
      if (url) {
        missing.set(textureKey, url);
      }
    }
    if (missing.size === 0) {
      return false;
    }
    if (this.loadingTextures) {
      // Already queued. A second request while the first is in flight would
      // register a second handler and rebuild twice for one batch.
      return true;
    }
    this.loadingTextures = true;
    for (const [key, url] of missing) {
      this.load.image(key, url);
    }
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.loadingTextures = false;
      if (this.shown) {
        this.rebuild();
      }
    });
    this.load.start();
    return true;
  }

  /** The docked fleet, on as many rings as it takes. */
  private buildFleet(centreX: number, centreY: number, baseRadius: number, count: number): void {
    const slots = ringLayout(count);
    const shipWidth = shipWidthFor(count, baseRadius, SHIP_MAX_WIDTH);
    const detail = detailFor(count);

    this.beams = this.track(this.add.graphics().setDepth(DEPTH.beams));
    // A second, tiny graphics for the ONE highlighted beam. Keeping it apart is
    // what lets the hundred static ones be drawn a single time.
    this.selectedBeam = this.track(this.add.graphics().setDepth(DEPTH.beams));

    slots.forEach((slot, index) => {
      const ship = this.ships[index];
      if (!ship) {
        return;
      }
      // The slot already carries its own ring's radius, so one multiplication
      // places a ship on any ring there is.
      const x = centreX + slot.x * baseRadius;
      const y = centreY + slot.y * baseRadius;
      const halo = this.track(
        this.add
          .circle(x, y, shipWidth * 0.72, 0xfacc15, 0)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(DEPTH.shipHalo),
      );
      const textureKey = `mining-${ship.assetKey}`;
      let sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
      if (this.textures.exists(textureKey)) {
        sprite = this.add.image(x, y, textureKey);
        (sprite as Phaser.GameObjects.Image).setDisplaySize(shipWidth, shipWidth);
      } else {
        /*
         * A hull-shaped stand-in until the texture arrives, and a permanent one
         * if the manifest has no art for it. A missing picture must never mean
         * a missing ship: the rings have to describe the fleet correctly even
         * while they are loading.
         */
        sprite = this.add.rectangle(x, y, shipWidth * 0.7, shipWidth, 0x94a3b8, 0.9);
      }
      /*
       * Pointing AT the planet. `slot.facing` is the direction from the ship to
       * the centre; the sprites are drawn nose-up, so a quarter turn puts the
       * nose along it.
       */
      sprite.setRotation(slot.facing + Math.PI / 2).setDepth(DEPTH.ship);
      this.track(sprite);
      this.shipViews.push({ instanceId: ship.instanceId, sprite, halo, x, y });

      // Where this ship's beam lands on the planet, and the flash it makes.
      // Above the LOD threshold the blooms are shared: a hundred of them on one
      // circle overlap into a ring nobody can pick a single impact out of.
      if (detail.perShipImpacts) {
        const toPlanet = Math.atan2(centreY - y, centreX - x);
        this.impacts.push(
          this.track(
            this.add
              .circle(
                centreX - Math.cos(toPlanet) * this.planetCentre.radius,
                centreY - Math.sin(toPlanet) * this.planetCentre.radius,
                Math.max(3, shipWidth * 0.16),
                0xe0f2fe,
                0.5,
              )
              .setBlendMode(Phaser.BlendModes.ADD)
              .setDepth(DEPTH.impact),
          ),
        );
      }

      // The sparks travelling this beam, allocated here and never in a frame.
      for (let particle = 0; particle < detail.particlesPerBeam; particle += 1) {
        this.sparks.push(
          this.track(
            this.add
              .circle(x, y, 2.6, 0x67e8f9, 0.9)
              .setBlendMode(Phaser.BlendModes.ADD)
              /*
               * IN FRONT of the hulls, and on a layer of their own. Sharing a
               * depth with the selection halos was wrong twice over: a spark
               * behind its own ship looks like a glitch, and "which halo is lit"
               * stopped being answerable because seventy sparks answered it too.
               */
              .setDepth(DEPTH.spark),
          ),
        );
        this.sparkProgress.push((particle + index * 0.37) / detail.particlesPerBeam);
      }
    });

    /*
     * The shared blooms, for a fleet too dense to give each ship its own.
     *
     * Eight of them, evenly round the planet, doing what a hundred overlapping
     * ones did: a lit rim where the beams land.
     */
    if (!detail.perShipImpacts && this.shipViews.length > 0) {
      for (let index = 0; index < SHARED_IMPACTS; index += 1) {
        const angle = (index / SHARED_IMPACTS) * Math.PI * 2;
        this.impacts.push(
          this.track(
            this.add
              .circle(
                centreX + Math.cos(angle) * this.planetCentre.radius,
                centreY + Math.sin(angle) * this.planetCentre.radius,
                Math.max(4, shipWidth * 0.22),
                0xe0f2fe,
                0.5,
              )
              .setBlendMode(Phaser.BlendModes.ADD)
              .setDepth(DEPTH.impact),
          ),
        );
      }
    }
  }

  /** How many sparks each beam is carrying right now. Diagnostics and tests. */
  particlesPerBeam(): number {
    return detailFor(this.shipViews.length).particlesPerBeam;
  }

  /** The halo under the selected ship, and nothing else. */
  private paintSelection(): void {
    for (const view of this.shipViews) {
      view.halo.setFillStyle(0xfacc15, view.instanceId === this.selectedInstanceId ? 0.35 : 0);
    }
    /*
     * The highlighted beam, redrawn only when the SELECTION changes.
     *
     * One line instead of re-stroking the whole fleet every frame to find the
     * one that is yellow.
     */
    const selectedBeam = this.selectedBeam;
    if (!selectedBeam) {
      return;
    }
    selectedBeam.clear();
    const view = this.shipViews.find(
      (candidate) => candidate.instanceId === this.selectedInstanceId,
    );
    if (!view) {
      return;
    }
    selectedBeam.lineStyle(3, 0xfacc15, 0.8);
    selectedBeam.lineBetween(view.x, view.y, this.planetCentre.x, this.planetCentre.y);
  }

  override update(_time: number, delta: number): void {
    if (!this.shown || !this.stage) {
      return;
    }
    this.elapsedMs += delta;
    const stage = this.stage;
    const { x: centreX, y: centreY, radius } = this.planetCentre;

    // The planet breathes. Presentation only: it is not a clock and nothing
    // reads it.
    const pulse = 1 + Math.sin(this.elapsedMs / 620) * 0.045;
    this.planetCore?.setScale(pulse);
    this.planetHalo?.setScale(1 + Math.sin(this.elapsedMs / 900) * 0.05);
    for (const [index, shell] of this.planetShells.entries()) {
      shell.setScale(1 + Math.sin(this.elapsedMs / (740 + index * 180)) * 0.05);
    }

    this.drawCracks(centreX, centreY, radius);
    this.drawFrame(stage);
    this.drawBeams(centreX, centreY);
    this.moveSparks(centreX, centreY);
    this.moveOrbitals(centreX, centreY);
  }

  /** Cracks across the body, breathing. Endpoints stay inside the circle. */
  private drawCracks(centreX: number, centreY: number, radius: number): void {
    const cracks = this.cracks;
    if (!cracks || radius <= 0) {
      return;
    }
    /*
     * Rebuilt eight times a second, not sixty.
     *
     * Seven fissures of three segments each is a geometry buffer rebuilt from
     * scratch every frame for a shape whose whole point is that it changes
     * SLOWLY. The pulse period is over half a second; at 125 ms the eye cannot
     * tell, and the frame does five fewer buffer rebuilds.
     */
    if (this.elapsedMs - this.cracksDrawnAtMs < 125) {
      return;
    }
    this.cracksDrawnAtMs = this.elapsedMs;
    cracks.clear();
    for (let index = 0; index < PLANET_CRACKS; index += 1) {
      const phase = Math.sin(this.elapsedMs / (520 + index * 90) + index);
      cracks.lineStyle(Math.max(1, radius * 0.02), 0xfef3c7, 0.24 + phase * 0.2);
      const angle = (index / PLANET_CRACKS) * Math.PI * 2 + index * 0.7;
      // A three-segment fissure, every point well inside the body.
      let x = centreX + Math.cos(angle) * radius * 0.12;
      let y = centreY + Math.sin(angle) * radius * 0.12;
      cracks.beginPath();
      cracks.moveTo(x, y);
      for (let segment = 1; segment <= 3; segment += 1) {
        const spread = angle + Math.sin(index + segment) * 0.45;
        const distance = (radius * 0.86 * segment) / 3;
        x = centreX + Math.cos(spread) * distance;
        y = centreY + Math.sin(spread) * distance;
        cracks.lineTo(x, y);
      }
      cracks.strokePath();
    }
  }

  /** The neon border, and the particles running it. */
  private drawFrame(stage: StageRect): void {
    const frame = this.frame;
    if (!frame) {
      return;
    }
    // The same pulse, applied to the whole stroke instead of re-stroking it.
    frame.setAlpha(0.45 + Math.sin(this.elapsedMs / 700) * 0.28);

    const perimeter = (stage.width + stage.height) * 2;
    for (const particle of this.frameParticles) {
      particle.progress = (particle.progress + (particle.speed * 16) / perimeter) % 1;
      const point = perimeterPoint(stage, particle.progress);
      particle.node.setPosition(point.x, point.y);
      particle.node.setAlpha(0.35 + Math.sin(this.elapsedMs / 300 + particle.progress * 12) * 0.4);
    }
  }

  /** One beam per ship, into one graphics object: one draw call for a hundred. */
  private drawBeams(_centreX: number, _centreY: number): void {
    // The beams themselves were painted once. All that moves per frame is how
    // brightly they glow and the blooms where they land.
    this.beams?.setAlpha(0.8 + Math.sin(this.elapsedMs / 520) * 0.2);
    for (const impact of this.impacts) {
      impact.setAlpha(0.3 + Math.sin(this.elapsedMs / 160 + impact.x) * 0.25);
    }
  }

  /** Sparks walking their beam and wrapping. Positions only: nothing allocated. */
  private moveSparks(centreX: number, centreY: number): void {
    const step = 16 / 1400;
    // The LOD decides how many sparks belong to each beam, so the mapping from
    // spark to ship has to ask it too.
    const perBeam = detailFor(this.shipViews.length).particlesPerBeam;
    for (let index = 0; index < this.sparks.length; index += 1) {
      const view = this.shipViews[Math.floor(index / perBeam)];
      const spark = this.sparks[index];
      if (!view || !spark) {
        continue;
      }
      const progress = ((this.sparkProgress[index] ?? 0) + step) % 1;
      this.sparkProgress[index] = progress;
      spark.setPosition(
        view.x + (centreX - view.x) * progress,
        view.y + (centreY - view.y) * progress,
      );
      spark.setAlpha(0.25 + (1 - progress) * 0.6);
    }
  }

  /** Particles orbiting the planet, drawn towards it as they go round. */
  private moveOrbitals(centreX: number, centreY: number): void {
    for (const orbital of this.orbitals) {
      orbital.angle += orbital.speed * 16;
      // Drawn IN: the orbit tightens and releases, so the planet reads as
      // something pulling matter towards itself rather than a decorated disc.
      const draw = 1 - Math.sin(this.elapsedMs / 1_800 + orbital.angle) * 0.12;
      orbital.node.setPosition(
        centreX + Math.cos(orbital.angle) * orbital.radius * draw,
        centreY + Math.sin(orbital.angle) * orbital.radius * draw,
      );
      orbital.node.setAlpha(0.3 + Math.sin(this.elapsedMs / 400 + orbital.angle * 3) * 0.35);
    }
  }

  /* ---- diagnostics, for the browser tests -------------------------------- */

  /** What the rings are currently drawing. */
  dockedCount(): number {
    return this.shipViews.length;
  }

  /** Where the planet is and how big, in canvas pixels. */
  planetGeometry(): { x: number; y: number; radius: number } {
    return { ...this.planetCentre };
  }
}

/** A point at `progress` around the stage's perimeter, clockwise from top-left. */
function perimeterPoint(stage: StageRect, progress: number): { x: number; y: number } {
  const perimeter = (stage.width + stage.height) * 2;
  let along = progress * perimeter;
  if (along < stage.width) {
    return { x: stage.x + along, y: stage.y };
  }
  along -= stage.width;
  if (along < stage.height) {
    return { x: stage.x + stage.width, y: stage.y + along };
  }
  along -= stage.height;
  if (along < stage.width) {
    return { x: stage.x + stage.width - along, y: stage.y + stage.height };
  }
  along -= stage.width;
  return { x: stage.x, y: stage.y + stage.height - along };
}
